import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { db } from "../lib/firebase";
import { api, callableErrorMessage } from "../lib/api";

export function Profile() {
  const { user, claims } = useAuth();
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName ?? "");
  }, [user]);

  useEffect(() => {
    if (!user || !claims.tenantId) return;
    return onSnapshot(
      doc(db, "tenants", claims.tenantId, "users", user.uid),
      (snap) => {
        const d = snap.data();
        if (d?.phone) setPhone(d.phone as string);
        if (d?.displayName && !displayName) setDisplayName(d.displayName as string);
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, claims.tenantId]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateCustomerProfile({
        displayName: displayName.trim(),
        phone: phone.trim()
      });
      if (displayName.trim() !== (user.displayName ?? "")) {
        await updateProfile(user, { displayName: displayName.trim() });
        await user.reload();
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col gap-6">
        <header className="flex items-center justify-between">
          <button onClick={() => nav(-1)} className="btn-ghost px-3 py-2 text-sm">
            ← Volver
          </button>
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">
            {tenant ? tenant.name : "Fideliza"}
          </p>
        </header>

        <div>
          <h1 className="font-display text-2xl font-semibold text-leaf-950">Tu perfil</h1>
          <p className="mt-1 text-sm text-leaf-700">Actualiza tu información personal.</p>
        </div>

        <form onSubmit={onSubmit} className="card flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Avatar user={user} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-leaf-950">
                {displayName || user?.email || "Sin nombre"}
              </p>
              <p className="truncate text-xs text-leaf-600">{user?.email ?? "Cuenta anónima"}</p>
            </div>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-leaf-700">Tu nombre</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Cómo te llamamos"
              className="input"
              maxLength={60}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-leaf-700">Teléfono (opcional)</span>
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+56 9 1234 5678"
              className="input"
              maxLength={20}
            />
            <span className="text-[11px] text-leaf-500">
              Lo usamos solo para que el cajero pueda buscarte si tu QR no funciona.
            </span>
          </label>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {saved && (
            <div className="rounded-2xl border border-leaf-200 bg-leaf-50 px-4 py-3 text-sm text-leaf-800">
              Guardado ✓
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </form>

        {user?.isAnonymous && (
          <section className="rounded-3xl border-2 border-dashed border-leaf-300 bg-leaf-50/60 p-5 text-center">
            <p className="text-xs uppercase tracking-wider text-leaf-700">Cuenta anónima</p>
            <h2 className="mt-2 font-display text-lg font-semibold text-leaf-950">
              Guarda tu progreso
            </h2>
            <p className="mt-2 text-sm text-leaf-800">
              Tus tickets están guardados solo en este dispositivo. Vincula tu cuenta con Google
              para no perderlos si cambias de celular.
            </p>
            <button onClick={() => nav("/upgrade")} className="btn-primary mt-4 w-full">
              Vincular con Google
            </button>
          </section>
        )}
      </div>
    </main>
  );
}

function Avatar({ user }: { user: ReturnType<typeof useAuth>["user"] }) {
  const initial = (user?.displayName ?? user?.email ?? "?").charAt(0).toUpperCase();
  if (user?.photoURL) {
    return (
      <img
        src={user.photoURL}
        alt=""
        className="h-14 w-14 rounded-full object-cover ring-2 ring-leaf-100"
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div className="grid h-14 w-14 place-items-center rounded-full bg-leaf-100 font-display text-2xl text-leaf-800 ring-2 ring-leaf-200">
      {initial}
    </div>
  );
}
